import React from "react";
import { Library, PenTool } from "lucide-react";

interface TeleprompterScriptProps {
  scriptMessage: string;
  setScriptMessage: (val: string) => void;
  showScript: boolean;
  setShowScript: (val: boolean) => void;
  textSize: number;
  scrollToRef: React.RefObject<HTMLTextAreaElement>;
  setVideoScriptModalOpen: (val: boolean) => void;
}

export const TeleprompterScript: React.FC<TeleprompterScriptProps> = ({
  scriptMessage,
  setScriptMessage,
  showScript,
  setShowScript,
  textSize,
  scrollToRef,
  setVideoScriptModalOpen,
}) => {
  return (
    <div className="w-full flex flex-col">
      {showScript && (
        <textarea
          ref={scrollToRef}
          style={{ fontSize: `${textSize * 1.5}px` }}
          key={"scriptText"}
          id={"scriptText"}
          value={scriptMessage}
          onChange={e => setScriptMessage(e.target.value)}
          className="w-[98%] h-[calc(100vh-230px)] pt-24 px-5 pb-[500px] rounded-xl border-2 border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 text-slate-800 dark:text-slate-100 text-left font-semibold resize-none outline-none focus:border-teal-500 dark:focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20 transition-all scrollbar-thin"
        />
      )}
      {!showScript && (
        <div className="flex flex-col w-full px-4 pt-4 gap-5 items-center">
          <p className="text-sm text-slate-500 font-medium select-none text-center max-w-sm leading-relaxed mb-1">
            Select an option below to set up your teleprompter script and guide your recording.
          </p>

          {/* Card 1: Choose from Library */}
          <button
            type="button"
            onClick={() => setVideoScriptModalOpen(true)}
            className="group w-full flex items-start gap-4.5 p-6 rounded-2xl border border-gray-200/80 bg-background hover:scale-[1.01] hover:border-teal-500/30 hover:shadow-sm transition-all duration-150 cursor-pointer text-left focus:outline-none"
          >
            <div className="p-3 rounded-xl bg-teal-50 text-teal-600 shrink-0 group-hover:bg-teal-100/80 transition-colors">
              <Library className="size-6" />
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <h4 className="text-base font-semibold text-slate-800 leading-tight">Choose from Library</h4>
              <p className="text-sm text-slate-500 leading-relaxed mt-1.5">
                Select a pre-written or coach-assigned script from your library. Perfect for standardized outlines and
                standardized prompts.
              </p>
              <span className="text-sm text-teal-600 font-bold mt-3.5 flex items-center gap-1 select-none">
                Browse library <span className="transition-transform group-hover:translate-x-0.5">→</span>
              </span>
            </div>
          </button>

          {/* Card 2: Write from Scratch */}
          <button
            type="button"
            onClick={() => setShowScript(true)}
            className="group w-full flex items-start gap-4.5 p-6 rounded-2xl border border-gray-200/80 bg-background hover:scale-[1.01] hover:border-teal-500/30 hover:shadow-sm transition-all duration-150 cursor-pointer text-left focus:outline-none"
          >
            <div className="p-3 rounded-xl bg-teal-50 text-teal-600 shrink-0 group-hover:bg-teal-100/80 transition-colors">
              <PenTool className="size-6" />
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <h4 className="text-base font-semibold text-slate-800 leading-tight">Write from Scratch</h4>
              <p className="text-sm text-slate-500 leading-relaxed mt-1.5">
                Type, edit, or paste your own custom script. Ideal for personal video messages, custom narratives, and
                impromptu updates.
              </p>
              <span className="text-sm text-teal-600 font-bold mt-3.5 flex items-center gap-1 select-none">
                Start writing <span className="transition-transform group-hover:translate-x-0.5">→</span>
              </span>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};
